import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import type { LocaleCode, RecordableOperation } from '@/api/models';
import { isoToDisplayDate } from '@/pages/accounting/statements/statementMeta';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type PickedOperationProps = {
    operation: RecordableOperation;
    locale: LocaleCode;
    /**
     * `null` when there is nothing to change to — this session may record
     * exactly one operation, and the header says so instead.
     */
    onChange: (() => void) | null;
};

/**
 * The operation being recorded, collapsed to one line, in the picker's place.
 *
 * **It takes the picker's place rather than sitting under it.** The form used to
 * render below the list; at sixty operations that put the first field the user
 * needs off screen. Collapsing the choice to a line and putting the form where
 * the list was makes the screen the same height whether the tenant has nine
 * operations or a hundred.
 *
 * **The subtitle is the recipe's provenance.** "Recipe v2 · effective since
 * 01/02/2026" is about the *version in force on the entry's date* — not the
 * template, which may have several — because that version is what the entry will
 * post under. The usage count is dropped when it is zero rather than rendered as
 * "0×", which reads as a verdict on the operation.
 *
 * **Change goes back to the picker with nothing carried over but the event.**
 * Slot answers belong to the old recipe's questions; carrying them silently
 * would post something nobody chose. The amount and the reference survive
 * because they are about the thing that happened, not about how it is booked.
 */
export const PickedOperation: React.FC<PickedOperationProps> = ({
    operation,
    locale,
    onChange,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('operations.record.picked.title')}
            className="flex items-start gap-3 rounded-2xl border border-separator bg-surface p-4"
            {...getNodeTestIdProp('operations-record-picked')}
        >
            <Icon
                aria-hidden
                className="mt-0.5 shrink-0 text-muted"
                icon="gravity-ui:receipt"
            />
            <div className="flex min-w-0 flex-1 flex-col">
                <span
                    className="text-sm font-medium text-foreground"
                    {...getNodeTestIdProp('operations-record-picked-name')}
                >
                    {locale === 'ka' ? operation.nameKa : operation.nameEn}
                </span>
                <span className="text-xs text-muted">
                    {locale === 'ka' ? operation.nameEn : operation.nameKa}
                </span>
                <span
                    className="mt-1 text-xs text-muted"
                    {...getNodeTestIdProp('operations-record-picked-recipe')}
                >
                    {t('operations.record.picked.recipe', {
                        version: operation.version ?? 1,
                    })}
                    {operation.effectiveFrom
                        ? t('operations.record.picked.since', {
                              date: isoToDisplayDate(operation.effectiveFrom),
                          })
                        : null}
                    {operation.recordCount > 0
                        ? t('operations.record.picked.used', {
                              count: operation.recordCount,
                          })
                        : null}
                </span>
                {onChange === null ? (
                    <span
                        className="mt-1 text-xs text-muted"
                        {...getNodeTestIdProp('operations-record-picked-sole')}
                    >
                        {t('operations.record.picked.soleOperation')}
                    </span>
                ) : null}
            </div>

            {onChange ? (
                <Button
                    onPress={onChange}
                    size="sm"
                    variant="outline"
                    {...getNodeTestIdProp('operations-record-picked-change')}
                >
                    {t('operations.record.picked.change')}
                </Button>
            ) : null}
        </section>
    );
};
